<!-- eslint-disable no-undef -->
<script setup>
import { ref, computed, onUnmounted } from 'vue';
import JsonEditor from '@/components/common/JsonEditor.vue';
import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
import Charger from '@/api/charger';
import { CHARGER_COMMANDS, COMMAND_GROUPS } from '@/config/chargerCommands';

const props = defineProps({
    dataID: { type: String, required: true },
});

const selectedCommand = ref('');
const payload = ref({});
const loading = ref(false);
const result = ref(null);
const resultType = ref(null);
let dismissTimer = null;
const payloadValid = ref(true);
const payloadSchema = ref(null);
const selectedVersion = ref('1.6');

onUnmounted(() => {
    if (dismissTimer) clearTimeout(dismissTimer);
});

const commandMeta = computed(() =>
    CHARGER_COMMANDS.find(c => c.command === selectedCommand.value) ?? null
);

const commandsByGroup = computed(() => {
    const map = {};
    for (const group of COMMAND_GROUPS) {
        const cmds = CHARGER_COMMANDS.filter(
            c => c.group === group && (c.version === selectedVersion.value || c.version === 'both')
        );
        if (cmds.length > 0) map[group] = cmds;
    }
    return map;
});

const showEditor = computed(() => !!selectedCommand.value);

function onCommandChange() {
    result.value = null;
    resultType.value = null;
    payloadValid.value = true;
    if (dismissTimer) clearTimeout(dismissTimer);
    if (!commandMeta.value) { payload.value = {}; payloadSchema.value = null; return; }
    const meta = commandMeta.value;
    const tmpl = (meta.version === 'both' && meta.template16)
        ? (selectedVersion.value === '1.6' ? meta.template16 : meta.template201)
        : (meta.template ?? {});
    payload.value = JSON.parse(JSON.stringify(tmpl));
    payloadSchema.value = (meta.version === 'both' && (meta.schema16 !== undefined || meta.schema201 !== undefined))
        ? (selectedVersion.value === '1.6' ? (meta.schema16 ?? null) : (meta.schema201 ?? null))
        : (meta.schema ?? null);
}

function onVersionChange(version) {
    if (version === selectedVersion.value) return;
    selectedVersion.value = version;
    selectedCommand.value = '';
    payload.value = {};
    payloadSchema.value = null;
    payloadValid.value = true;
    result.value = null;
    resultType.value = null;
    if (dismissTimer) clearTimeout(dismissTimer);
}

function resetForm() {
    selectedCommand.value = '';
    payload.value = {};
    payloadSchema.value = null;
    result.value = null;
    resultType.value = null;
    payloadValid.value = true;
    if (dismissTimer) clearTimeout(dismissTimer);
}

const ERROR_MESSAGES = {
    404: 'Charger is not currently connected',
    502: 'Charger rejected the command',
    504: 'Charger did not respond in time',
    400: 'Unknown command or invalid payload',
};

async function sendCommand() {
    loading.value = true;
    result.value = null;
    resultType.value = null;
    if (dismissTimer) clearTimeout(dismissTimer);

    try {
        const response = await Charger.sendCommand(props.dataID, selectedCommand.value, payload.value);
        if (response.status === 200) {
            const data = await response.json();
            result.value = JSON.stringify(data, null, 2);
            resultType.value = 'success';
            dismissTimer = setTimeout(() => {
                result.value = null;
                resultType.value = null;
            }, 5000);
        } else {
            result.value = ERROR_MESSAGES[response.status] ?? `Unexpected error (${response.status})`;
            resultType.value = 'error';
        }
    } catch {
        result.value = 'Network error — could not reach the API';
        resultType.value = 'error';
    } finally {
        loading.value = false;
    }
}
</script>

<template>
    <div class="flex flex-col gap-4">
        <!-- Version toggle -->
        <div>
            <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">OCPP Version</div>
            <div class="flex gap-1.5">
                <button
                    v-for="v in ['1.6', '2.0.1']"
                    :key="v"
                    type="button"
                    :disabled="loading"
                    class="px-3 py-1.5 rounded-mc-sm border font-mono text-xs font-bold uppercase tracking-[0.06em] transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-blue/40 disabled:opacity-50"
                    :class="selectedVersion === v
                        ? 'border-mc-red bg-mc-red text-white'
                        : 'border-mc-steel dark:border-line-on-dark text-ink-muted dark:text-on-dark-muted hover:border-mc-ink dark:hover:border-on-dark-muted bg-transparent'"
                    @click="onVersionChange(v)"
                >
                    {{ v }}
                </button>
            </div>
        </div>

        <!-- Command select -->
        <div>
            <label
                for="charger-command-select"
                class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2 block"
            >
                Command
            </label>
            <select
                id="charger-command-select"
                v-model="selectedCommand"
                :disabled="loading"
                class="w-full px-3 py-2 rounded-mc-sm border border-mc-steel dark:border-line-on-dark bg-mc-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark font-body text-sm focus:outline-none focus:ring-[3px] focus:ring-mc-blue/40 disabled:opacity-50"
                @change="onCommandChange"
            >
                <option value="" disabled>Select a command…</option>
                <template v-for="group in COMMAND_GROUPS" :key="group">
                    <optgroup v-if="commandsByGroup[group]" :label="group">
                        <option
                            v-for="cmd in commandsByGroup[group]"
                            :key="cmd.command"
                            :value="cmd.command"
                        >
                            {{ cmd.label }}
                        </option>
                    </optgroup>
                </template>
            </select>
        </div>

        <!-- Payload editor -->
        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
        >
            <div
                v-if="showEditor"
                class="bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-md p-3"
            >
                <div class="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-2">Payload</div>
                <JsonEditor
                    v-model:json-value="payload"
                    title="Payload"
                    :advanced="true"
                    :schema="payloadSchema"
                    @update:valid="payloadValid = $event"
                />
            </div>
        </transition>

        <!-- Send row -->
        <div class="flex items-center gap-3 pt-1 border-t border-mc-steel/50 dark:border-line-on-dark">
            <button
                type="button"
                class="btn-primary"
                :disabled="!selectedCommand || loading || !payloadValid"
                @click="sendCommand"
            >
                {{ loading ? 'Sending…' : 'Send Command' }}
            </button>
            <button
                v-if="selectedCommand"
                type="button"
                class="btn-secondary"
                :disabled="loading"
                @click="resetForm"
            >
                Reset
            </button>
            <LoadingAnimation3 v-if="loading" size="24px" />
        </div>

        <!-- Result display -->
        <transition
            enter-active-class="transition-all duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-150 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="result"
                class="rounded-mc-sm border p-3"
                :class="resultType === 'success'
                    ? 'border-green-500/40 bg-green-500/5 text-green-700 dark:text-green-400'
                    : 'border-mc-red/40 bg-mc-red/5 text-mc-red'"
            >
                <pre class="font-mono text-xs whitespace-pre-wrap break-all">{{ result }}</pre>
            </div>
        </transition>
    </div>
</template>
